import {
  Component, ChangeDetectionStrategy, inject, NgZone,
  OnDestroy, signal, computed,
} from '@angular/core';

@Component({
  selector: 'widget-kronometr',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="kronometr" [class.calisiyor]="calisiyor()">
      <div class="gosterim">{{ gosterim() }}</div>
      <div class="butonlar">
        <button class="btn baslat" (click)="toggle()" [class.aktif]="calisiyor()">
          {{ calisiyor() ? '⏸' : '▶' }}
        </button>
        <button class="btn tur" (click)="turEkle()" [disabled]="!calisiyor()">🏁</button>
        <button class="btn sifirla" (click)="sifirla()" [disabled]="calisiyor() && gecenSure() === 0">⏹</button>
      </div>
      @if (turlar().length) {
        <div class="tur-listesi">
          @for (t of sonTurlar(); track $index) {
            <div class="tur-satir">
              <span class="tur-no">{{ turlar().length - $index }}</span>
              <span class="tur-sure">{{ formatSure(t) }}</span>
              @if ($index < sonTurlar().length - 1) {
                <span class="tur-fark">+{{ formatSure(t - sonTurlar()[$index + 1]) }}</span>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .kronometr { text-align: center; font-family: 'SF Mono', Consolas, monospace; padding: 8px 0; }
    .gosterim {
      font-size: 2.6em; font-weight: 700; letter-spacing: 2px;
      color: var(--py-metin, #e0e0e0); padding: 12px 0 16px;
      transition: text-shadow 0.3s;
    }
    .calisiyor .gosterim {
      animation: nabiz 1.2s ease-in-out infinite;
      text-shadow: 0 0 18px rgba(82, 196, 26, 0.5);
    }
    .butonlar { display: flex; justify-content: center; gap: 12px; margin-bottom: 12px; }
    .btn {
      width: 44px; height: 44px; border-radius: 50%; border: 2px solid var(--py-sinir, #333);
      background: var(--py-arkaplan-ikincil, #1a1a2e); color: var(--py-metin, #e0e0e0);
      font-size: 1.2em; cursor: pointer; transition: all 0.2s; display: flex;
      align-items: center; justify-content: center; padding: 0;
    }
    .btn:hover:not(:disabled) { transform: scale(1.1); border-color: var(--py-ana-renk, #1890ff); }
    .btn:active:not(:disabled) { transform: scale(0.95); }
    .btn:disabled { opacity: 0.3; cursor: not-allowed; }
    .btn.baslat.aktif { border-color: #52c41a; background: rgba(82, 196, 26, 0.15); }
    .btn.sifirla:hover:not(:disabled) { border-color: #f5222d; }
    .tur-listesi {
      max-height: 140px; overflow-y: auto; margin-top: 4px;
      scrollbar-width: thin; scrollbar-color: var(--py-sinir, #333) transparent;
    }
    .tur-satir {
      display: flex; align-items: center; gap: 8px; padding: 4px 12px;
      font-size: 0.8em; border-bottom: 1px solid rgba(255,255,255,0.05);
    }
    .tur-satir:hover { background: rgba(255,255,255,0.03); }
    .tur-no {
      min-width: 22px; height: 22px; border-radius: 50%;
      background: var(--py-ana-renk, #1890ff); color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 0.8em; font-weight: 600;
    }
    .tur-sure { flex: 1; text-align: left; color: var(--py-metin, #e0e0e0); }
    .tur-fark { color: var(--py-metin-ikincil, #aaa); font-size: 0.9em; opacity: 0.6; }
    @keyframes nabiz {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `],
})
export class KronometrComponent implements OnDestroy {
  private zone = inject(NgZone);

  calisiyor = signal(false);
  gecenSure = signal(0);
  turlar = signal<number[]>([]);

  private baslangicZamani = 0;
  private birikimMs = 0;
  private interval: any;

  gosterim = computed(() => this.formatSure(this.gecenSure()));
  sonTurlar = computed(() => [...this.turlar()].reverse().slice(0, 5));

  toggle() {
    if (this.calisiyor()) {
      this.durdur();
    } else {
      this.baslat();
    }
  }

  private baslat() {
    this.baslangicZamani = Date.now();
    this.calisiyor.set(true);
    this.zone.runOutsideAngular(() => {
      this.interval = setInterval(() => {
        this.gecenSure.set(this.birikimMs + Date.now() - this.baslangicZamani);
      }, 10);
    });
  }

  private durdur() {
    clearInterval(this.interval);
    this.birikimMs += Date.now() - this.baslangicZamani;
    this.gecenSure.set(this.birikimMs);
    this.calisiyor.set(false);
  }

  sifirla() {
    clearInterval(this.interval);
    this.birikimMs = 0;
    this.baslangicZamani = 0;
    this.gecenSure.set(0);
    this.calisiyor.set(false);
    this.turlar.set([]);
  }

  turEkle() {
    if (!this.calisiyor()) return;
    this.turlar.update(t => [...t, this.gecenSure()]);
  }

  formatSure(ms: number): string {
    const dk = Math.floor(ms / 60000);
    const sn = Math.floor((ms % 60000) / 1000);
    const sl = Math.floor((ms % 1000) / 10);
    return `${String(dk).padStart(2, '0')}:${String(sn).padStart(2, '0')}:${String(sl).padStart(2, '0')}`;
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }
}
